"use client";

import Link from "next/link";
import { useEffect } from "react";

const font = 'var(--font-geist-sans), system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    fetch("/api/internal/error-report", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: error?.message || "Unhandled app error",
        digest: error?.digest,
        url: typeof window !== "undefined" ? window.location.href : "",
        route: typeof window !== "undefined" ? window.location.pathname : "",
      }),
    }).catch(() => {});
  }, [error]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem",
        fontFamily: font,
        background: "linear-gradient(165deg, #ffffff 0%, #f0f7ff 45%, #e8f4fc 100%)",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "420px",
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "1px solid rgba(203,213,225,0.6)",
          borderRadius: "16px",
          padding: "2.25rem 2rem",
          boxShadow: "0 4px 24px rgba(15,23,42,0.08)",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "2.5rem", marginBottom: "1rem", lineHeight: 1 }} aria-hidden>
          🔧
        </div>

        <h1
          style={{
            fontSize: "1.2rem",
            fontWeight: 800,
            margin: "0 0 0.65rem",
            color: "#0c4a6e",
            letterSpacing: "-0.02em",
          }}
        >
          Something went wrong on our end
        </h1>

        <p
          style={{
            margin: "0 0 1.5rem",
            fontSize: "0.9375rem",
            lineHeight: 1.6,
            color: "#64748b",
          }}
        >
          Your stock data is safe &mdash; this is a display glitch, not a data problem.
          We&rsquo;ve been automatically alerted. A refresh fixes it most of the time.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          <button
            onClick={reset}
            style={{
              width: "100%",
              padding: "0.8rem 1rem",
              background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
              color: "#fff",
              fontFamily: font,
              fontWeight: 700,
              fontSize: "0.9375rem",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
            }}
          >
            Try again
          </button>

          <Link
            href="/dashboard"
            style={{
              display: "block",
              width: "100%",
              padding: "0.75rem 1rem",
              background: "transparent",
              border: "1.5px solid #cbd5e1",
              borderRadius: "10px",
              color: "#475569",
              fontFamily: font,
              fontWeight: 600,
              fontSize: "0.875rem",
              textDecoration: "none",
              boxSizing: "border-box",
            }}
          >
            Go to dashboard
          </Link>
        </div>

        {error?.digest && (
          <p
            style={{
              marginTop: "1.25rem",
              fontSize: "0.7rem",
              color: "#94a3b8",
              fontFamily: "monospace",
            }}
          >
            Ref: {error.digest}
          </p>
        )}
      </div>
    </div>
  );
}
